import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { testimonialsPublic } from "../../lib/api";
import { useSeo } from "../../lib/useSeo";
import { Avatar } from "../../lib/Avatar";
import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Pill } from "../../lib/Pill";
import { PlanGrid, Slats } from "../../lib/Slats";
import { HeroSignupCard } from "./HeroSignupCard";
import { PilotPromoSection } from "./PilotPromoSection";

export function HomePage() {
  useSeo({
    title: "NestPlot — Run the property, not the software.",
    description:
      "Property operations software for multifamily, self-storage, parking, and RV/boat. 100% of tenant rent lands in your bank — flat monthly pricing, no per-door, no processing margin.",
    path: "/",
    // Organization + WebSite JSON-LD (SEO audit P1). Google
    // Knowledge Panel + brand-search rich result eligibility.
    // SoftwareApplication already lives in index.html — kept there
    // because it's the same graph for every route.
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "NestPlot",
        url: "https://nestplot.com",
        logo: "https://nestplot.com/brand/nestplot-mark-512.png",
        sameAs: [
          "https://github.com/efossi/nestplot",
        ],
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer service",
          email: "info@nestplot.com",
          areaServed: "US",
          availableLanguage: "en",
        },
        address: {
          "@type": "PostalAddress",
          streetAddress: "16192 Coastal Highway",
          addressLocality: "Lewes",
          addressRegion: "DE",
          postalCode: "19958",
          addressCountry: "US",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "NestPlot",
        url: "https://nestplot.com",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://nestplot.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
    ],
  });
  const testimonials = useQuery({
    queryKey: ["public", "testimonials"],
    queryFn: () => testimonialsPublic.list(6),
    staleTime: 5 * 60_000,
  });
  const rows = testimonials.data ?? [];
  return (
    <div>
      {/* Hero — vertical-picker. Lead with "pick the property you run"
          so prospects route straight to a vertical-native page instead
          of reading a four-vertical brochure pitch on day 1.
          See docs/MARKETING_PLAN.md §3 — multi-vertical hero. */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12 lg:py-16">
          <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 items-start">
            <div>
              <div className="flex flex-wrap gap-2">
                <Pill tone="accent" size="sm">
                  Property operations · pick what you run
                </Pill>
                <Pill tone="ok" size="sm">
                  US · Canada · UK · Ireland · Australia · NZ
                </Pill>
              </div>
              <h1 className="t-display-xl mt-5">
                Run the property,
                <br />
                <span className="text-accent">not the software.</span>
              </h1>
              <p className="text-lg text-sub mt-5 max-w-[540px]">
                One operations platform that doesn't take a cut of your rent.
                Tenant rent settles into your bank account through your own
                Stripe — we never touch it. Flat monthly pricing in your local
                currency, no per-door, no setup fee. Pick your vertical to see
                how it fits.
              </p>
              <div className="mt-7 flex flex-wrap gap-6 text-sm">
                <Stat label="Setup fee" value="$0" />
                <Stat label="Cut on tenant rent" value="0%" />
                <Stat label="Sales-call gate" value="None" />
              </div>
              <div className="mt-7 flex flex-wrap gap-3 items-center">
                <Link to="/signup">
                  <Btn kind="primary" size="lg" iconRight={<Icon name="arrow-right" size={16} />}>
                    Start trial
                  </Btn>
                </Link>
                <Link to="/book-demo">
                  <Btn kind="secondary" size="lg" icon={<Icon name="calendar" size={16} />}>
                    Book a live demo
                  </Btn>
                </Link>
              </div>
            </div>
            <div>
              <HeroSignupCard />
            </div>
          </div>

          {/* Vertical picker — the primary CTA. Each card routes to a
              vertical-native landing page with the right competitor
              comparison + feature lens + free tools. */}
          <div className="mt-10">
            <div className="font-mono text-[11px] text-sub uppercase tracking-[0.16em] mb-3">
              Pick what you operate
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Link to="/for-multifamily" className="block">
                <VerticalCard
                  vertical="multifamily"
                  label="Multifamily"
                  sub="Homes nest in floors over a lobby."
                  meta="Single building to nationwide portfolios"
                  kpi={["Leased", "96%"]}
                />
              </Link>
              <Link to="/for-storage" className="block">
                <VerticalCard
                  vertical="storage"
                  label="Self-storage"
                  sub="Units nest in rows along a drive aisle."
                  meta="One facility to nationwide operators"
                  kpi={["Occupancy", "91.2%"]}
                />
              </Link>
              <Link to="/for-parking" className="block">
                <VerticalCard
                  vertical="parking"
                  label="Parking & lots"
                  sub="Stalls nest in striped rows."
                  meta="Surface + structured · permits"
                  kpi={["Permits", "340"]}
                />
              </Link>
              <Link to="/for-marina" className="block">
                <VerticalCard
                  vertical="rv"
                  label="Marina & RV"
                  sub="Slips nest along a dock; rigs nest off a lane."
                  meta="Slip + dry-stack + RV pull-through"
                  kpi={["Slips open", "12"]}
                />
              </Link>
            </div>
            <div className="mt-5 flex flex-wrap gap-3 items-center">
              <Link to="/signup">
                <Btn kind="primary" size="md" iconRight={<Icon name="arrow-right" size={14} />}>
                  Start free · 30 days
                </Btn>
              </Link>
              <Link to="/book-demo">
                <Btn kind="secondary" size="md" icon={<Icon name="calendar" size={14} />}>
                  Book a live demo
                </Btn>
              </Link>
              <Link to="/product" className="text-accent font-semibold text-sm">
                See how it works →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <PilotPromoSection />

      <section className="bg-surface border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12 grid md:grid-cols-3 gap-6">
          <Feature
            icon="grid"
            title="In-product e-signature"
            body="50-state lease engine with a court-ready audit trail. State-specific templates auto-generated from the statute that governs your property — landlord-tenant act, self-storage statute, parking lease law, or marina/RV lot agreement. Signing happens in-product; no separate tenant app to install."
          />
          <Feature
            icon="wallet"
            title="Stripe Connect built in"
            body="Tenant rent — or resident rent, or boater dock fees, or parking permits — settles directly to your bank. You keep your processing margin. We never touch it."
          />
          <Feature
            icon="chart"
            title="Pricing AI · auto-apply optional"
            body="Watches occupancy and suggests rate moves within the bounds you set. Stays advisory by default — you approve every recommendation until you decide otherwise. Works the same across storage units, apartments, slips, and stalls."
          />
        </div>
      </section>

      <section className="border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            Comparison
          </div>
          <h2 className="t-display-l mt-2">Built differently on every line that matters</h2>
          <p className="text-sub mt-2 max-w-[560px]">
            The vendors that own this market either hide their pricing, route your tenants'
            rent through their own merchant accounts, or both. We don't do either.{" "}
            <Link to="/vs" className="text-accent font-semibold">See every comparison →</Link>
          </p>

          <div className="mt-8 overflow-x-auto">
            <table className="w-full border border-line rounded-lg bg-paper text-sm overflow-hidden">
              <thead>
                <tr className="bg-surface text-sub font-mono text-[11px] tracking-[0.08em] uppercase">
                  <th scope="col" className="text-left px-5 py-3 font-medium">Feature</th>
                  <th scope="col" className="text-left px-5 py-3 font-medium">storEDGE</th>
                  <th scope="col" className="text-left px-5 py-3 font-medium">SiteLink</th>
                  <th scope="col" className="text-left px-5 py-3 font-medium">
                    <span className="text-accent">NestPlot</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <Row k="Published price" v={["Contact sales", "Contact sales", "From $59/mo flat"]} />
                <Row k="Payment processor" v={["Storable Payments", "SL Merchant Services", "Your Stripe · 0% cut"]} />
                <Row k="Setup fee" v={["$1k–$5k reported", "$500–$1.5k", "$0"]} />
                <Row k="Gate integrations" v={["Storable ecosystem", "Storable ecosystem", "PTI · SecurGuard · INSOMNIAC"]} />
                <Row k="State-specific lease" v={["Yes", "Yes", "All 50 + DC · court-ready audit trail"]} />
                <Row k="Sales call required" v={["Yes", "Yes", "No · sign up online"]} />
                <Row k="QuickBooks Online sync" v={["Add-on", "Add-on", "Included"]} />
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            Built for your state's law
          </div>
          <h2 className="t-display-l mt-2">All 50 states + DC, statute-cited.</h2>
          <p className="text-sub mt-2 max-w-[600px]">
            NestPlot generates a state-compliant lease from the statute that
            governs your property — landlord-tenant act for apartments,
            self-storage statute for storage, parking lease law for lots, or
            marina/RV agreements — the moment you create a facility. Late-fee
            cap, grace period, notice windows — all wired in.
          </p>
          <div className="mt-6 grid sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {[
              { slug: "california", name: "California" },
              { slug: "texas", name: "Texas" },
              { slug: "florida", name: "Florida" },
              { slug: "new-york", name: "New York" },
              { slug: "illinois", name: "Illinois" },
              { slug: "georgia", name: "Georgia" },
              { slug: "pennsylvania", name: "Pennsylvania" },
              { slug: "ohio", name: "Ohio" },
              { slug: "north-carolina", name: "North Carolina" },
              { slug: "michigan", name: "Michigan" },
              { slug: "arizona", name: "Arizona" },
              { slug: "washington", name: "Washington" },
            ].map((s) => (
              <Link
                key={s.slug}
                to={`/states/${s.slug}`}
                className="border border-line rounded-md px-3 py-2 text-sm font-semibold hover:bg-paper hover:border-line-strong transition-colors"
              >
                {s.name} →
              </Link>
            ))}
          </div>
          <div className="mt-5">
            <Link to="/states" className="text-accent font-semibold">See all 50 states + DC →</Link>
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-surface">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            Free tools
          </div>
          <h2 className="t-display-l mt-2">Statute-cited calculators. No signup.</h2>
          <p className="text-sub mt-2 max-w-[620px]">
            Dozens of free tools across multifamily, self-storage, parking, and
            RV/boat — built from the same per-state statute tables NestPlot
            uses internally. Plug in a state and your numbers — get the answer
            in seconds. Customer or not.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { slug: "security-deposit-cap", name: "Security deposit cap", blurb: "Multifamily · the maximum deposit your state lets you collect." },
              { slug: "eviction-timeline-builder", name: "Eviction timeline", blurb: "Multifamily · per-state notice and court-filing windows." },
              { slug: "late-fee-calculator", name: "Late-fee calculator", blurb: "Storage · the maximum legal fee for your state and rent." },
              { slug: "lien-sale-timeline", name: "Lien-sale timeline", blurb: "Storage · the notice dates from last payment." },
              { slug: "parking-permit-pricing", name: "Parking permit pricing", blurb: "Parking · benchmark monthly permit prices by market." },
              { slug: "parking-rate-card-builder", name: "Parking rate-card builder", blurb: "Parking · the tiered rate card for a lot or garage." },
              { slug: "slip-loa-pricing-calculator", name: "Slip LOA pricing", blurb: "Marina · the per-foot pricing for boat slips by LOA." },
              { slug: "rv-storage-size-guide", name: "RV storage size guide", blurb: "RV · the right bay size for class A/B/C, fifth wheels." },
            ].map((t) => (
              <Link key={t.slug} to={`/tools/${t.slug}`} className="block group">
                <Card className="h-full hover:shadow-rollup transition-shadow">
                  <div className="px-5 py-4">
                    <h3 className="font-display font-extrabold text-[15px] uppercase tracking-[-0.005em]">
                      {t.name}
                    </h3>
                    <p className="text-sub text-[12.5px] mt-2">{t.blurb}</p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
          <div className="mt-5">
            <Link to="/tools" className="text-accent font-semibold">See all free tools →</Link>
          </div>
        </div>
      </section>

      {rows.length > 0 && (
        <section className="border-b border-line bg-bg">
          <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-14">
            <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
              Loved by operators
            </div>
            <h2 className="t-display-l mt-2">From the field</h2>
            <div className="grid md:grid-cols-3 gap-4 mt-8">
              {rows.map((t, i) => (
                <Card key={`${t.name}-${i}`}>
                  <div className="bg-accent px-5 pt-3 pb-2">
                    <Slats color="var(--on-accent)" opacity={0.55} height={10} />
                  </div>
                  <div className="px-5 py-5">
                    <p className="text-[15px] leading-relaxed">"{t.quote}"</p>
                    <div className="flex items-center gap-3 mt-5">
                      <Avatar initials={t.initials} color="var(--accent-2)" />
                      <div>
                        <div className="font-semibold">{t.name}</div>
                        <div className="text-xs text-sub">{t.role}</div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      <section>
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-14 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <h2 className="t-display-l">
              Migrate this weekend.<br />
              <span className="text-accent">Save $850 next month.</span>
            </h2>
            <p className="text-sub mt-3 max-w-[480px]">
              30-day free trial. No card required at signup. Bring your roster
              as a CSV — AppFolio, Yardi, DoorLoop, storEDGE, SiteLink,
              Marina Office Manager, ParkMobile exports, or a Google Sheet —
              and we'll help map the columns.
            </p>
            <div className="mt-6 flex gap-3 flex-wrap">
              <Link to="/signup">
                <Btn kind="primary" size="lg" iconRight={<Icon name="arrow-right" size={16} />}>
                  Start trial
                </Btn>
              </Link>
              <Link to="/book-demo">
                <Btn kind="secondary" size="lg" icon={<Icon name="calendar" size={16} />}>
                  Book a live demo
                </Btn>
              </Link>
            </div>
          </div>
          <Card>
            <div className="bg-ink px-5 pt-3 pb-2">
              <Slats color="var(--accent)" opacity={0.7} height={10} />
            </div>
            <div className="px-5 py-5">
              <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
                Migration included
              </div>
              <div className="font-display font-black text-[32px] tracking-[-0.025em] mt-1">
                We do the migration for you
              </div>
              <p className="text-sub text-sm mt-2">
                The NestPlot team runs your data migration — included on every
                plan, no setup fee. Start your 30-day trial, no card.
              </p>
              <Link to="/pilot" className="block mt-4">
                <Btn kind="primary" full size="md">
                  Plan my migration
                </Btn>
              </Link>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}

function Feature({ icon, title, body }: { icon: "grid" | "wallet" | "chart"; title: string; body: string }) {
  return (
    <div>
      <div className="w-10 h-10 rounded-md bg-paper border border-line grid place-items-center text-accent">
        <Icon name={icon} size={18} />
      </div>
      <h3 className="font-display font-extrabold text-[18px] uppercase tracking-[-0.01em] mt-3">
        {title}
      </h3>
      <p className="text-sub text-sm mt-1 max-w-[360px]">{body}</p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-mono text-[10px] text-sub tracking-[0.1em] uppercase">{label}</div>
      <div className="font-display font-extrabold text-[22px] tracking-[-0.02em] mt-[2px]">
        {value}
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string[] }) {
  return (
    <tr className="border-t border-line">
      <th scope="row" className="text-left px-5 py-3 font-semibold">{k}</th>
      <td className="px-5 py-3 text-sub">{v[0]}</td>
      <td className="px-5 py-3 text-sub">{v[1]}</td>
      <td className="px-5 py-3 font-semibold text-accent">{v[2]}</td>
    </tr>
  );
}

/** "One plan, every vertical" card — drafting-grammar PlanGrid above the
 * label + meta + KPI. Renders the same SVG language for every vertical;
 * only the parcel subdivision + per-vertical accent changes. Per-vertical
 * accents match the design canvas (Plot Green / Harbor Blue / Violet /
 * Brick) and the suggestedAccent map in lib/verticals.ts. */
const VERTICAL_HOMEPAGE_ACCENTS: Record<
  "storage" | "multifamily" | "rv" | "parking",
  string
> = {
  storage: "#B5543B",     // Brick
  multifamily: "#3F6B4B", // Plot Green
  rv: "#2D6E8F",          // Harbor Blue
  parking: "#6C5AA8",     // Violet
};

function VerticalCard({
  vertical,
  label,
  sub,
  meta,
  kpi,
}: {
  vertical: "storage" | "multifamily" | "rv" | "parking";
  label: string;
  sub: string;
  meta: string;
  kpi: [string, string];
}) {
  const accent = VERTICAL_HOMEPAGE_ACCENTS[vertical];
  return (
    <Card>
      <div className="bg-surface px-4 pt-4 pb-3 border-b border-line">
        <PlanGrid
          vertical={vertical}
          height={130}
          accent={accent}
          ariaLabel={`${label} plan view`}
        />
      </div>
      <div className="px-5 py-5">
        <h3 className="font-display font-extrabold text-[16px] uppercase tracking-[-0.005em]">
          {label}
        </h3>
        <p className="text-sub text-[13px] mt-2 leading-relaxed">{sub}</p>
        <div className="font-mono text-[10.5px] text-sub uppercase tracking-[0.12em] mt-4 pt-3 border-t border-line">
          {meta}
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="font-mono text-[10px] text-sub uppercase tracking-[0.14em]">
            {kpi[0]}
          </span>
          <span
            className="font-display font-black text-[18px] tracking-[-0.02em]"
            style={{ color: accent }}
          >
            {kpi[1]}
          </span>
        </div>
      </div>
    </Card>
  );
}
